import { TranslateService } from '@ngx-translate/core';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { MenuPage } from '../menu/menu.page';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrderType } from 'src/smoothr-web-app-core/enums/OrderType';
import { ModalController } from '@ionic/angular';
import Table from '../../../smoothr-web-app-core/models/Table';
import { firstValueFrom, interval } from 'rxjs';
import { DeinstagModal, DeinstagModalResult } from '../../components/deinstag-modal/deinstag-modal.component';
import { SmoothrCNCDiscount } from '../../../smoothr-web-app-core/models/CNCDiscount';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GuestCountModalComponent } from '../../modals/guest-count-modal/guest-count-modal.component';
import { ArticleType, getArticleType } from '../../../smoothr-web-app-core/utils/utils';
import { AutomaticPromoModal } from '../../components/automatic-promo-modal/automatic-promo-modal.component';
import { ChecksResponse } from '../../../smoothr-web-app-core/models/ChecksResponse';
import { CheckStatus } from '../../../smoothr-web-app-core/enums/CheckStatus';

@Component({
	selector: 'app-home',
	templateUrl: './home.page.html',
	styleUrls: ['./home.page.scss'],
	standalone: false,
})
export class HomePage implements OnInit {
	static url = 'home';
	static scanUrl = 'venue/:venueId/table/:tableNumber';

	environment = environment;
	version = environment.version;

	public venue: Venue | null = null;
	public table: Table | null = null;
	checksResponse: ChecksResponse | null = null;

	loading = true;
	protected venueReadableId: string | null = null;
	protected tableNumber: string | null = null;
	private translate = inject(TranslateService);
	private repositoryService = inject(RepositoryService);
	private activatedRoute = inject(ActivatedRoute);
	private modalCtrl = inject(ModalController);
	private router = inject(Router);
	private snackBar = inject(MatSnackBar);
	private destroyRef = inject(DestroyRef);

	cncDiscounts = this.repositoryService.availableDiscounts$;

	constructor() {}

	static async navigate(router: Router) {
		await router.navigateByUrl(HomePage.url);
	}

	ngOnInit() {
		this.loading = true;
		this.handleInit();
	}

	async handleDiscounts(discounts: SmoothrCNCDiscount[]) {
		const deinstag = discounts.find(it => it.id === environment.deinstagId);
		if (deinstag) {
			console.log({
				deinstag,
			});
			this.loading = true;
			try {
				const deinstagsPromoCode = await this.repositoryService.getDeinstagPromoCode();
				if (!deinstagsPromoCode) {
					this.loading = false;
					return;
				}
				const checks = await this.repositoryService.getChecksOfTable(this.venueReadableId, this.tableNumber);
				const tableHasDeinstag = checks.checks.find(
					it => !!it.checkoutOrderedArticles.find(it => environment.deinstagPlus.find(dplu => dplu === it.sku))
				);
				if (tableHasDeinstag) {
					this.loading = false;
					return;
				}
				const tableHasGameplay = checks.checks.find(
					it => !!it.checkoutOrderedArticles.find(it => getArticleType(it.sku) === ArticleType.GAMEPLAY)
				);
				if (!tableHasGameplay) {
					this.loading = false;
					return;
				}
				const result = await DeinstagModal.show(this.modalCtrl);
				if (result === DeinstagModalResult.CONFIRM) {
					const guestCount = await GuestCountModalComponent.show(this.modalCtrl, 3, 6, 3);
					this.loading = true;
					try {
						await this.repositoryService.sendDeinstag(
							this.venue,
							null,
							deinstagsPromoCode,
							OrderType.STANDARD,
							null,
							guestCount,
							this.table
						);
					} catch (e) {
						console.error(e);
					}
					await this.goToMenu();
				}
			} catch (e) {
				console.info(e);
				this.loading = false;
			}
		}
	}

	async handleInit() {
		try {
			console.log('HomePage.handleInit()');
			const params = await firstValueFrom(this.activatedRoute.params);
			this.venueReadableId = params['venue'] ?? null;
			this.tableNumber = params['table'] ?? null;
			console.log(params);

			// Check for the auto-order query parameter. `order=true` is sent by the pay app's
			// "Bestellen" button so we start ordering directly (skip the extra tap), mirroring the
			// `participate=true` param we send the pay app on our "Pay" button.
			const queryParams = await firstValueFrom(this.activatedRoute.queryParams);
			console.log('Query params:', queryParams);
			const shouldAutoOrder = queryParams['order'] === 'true';
			console.log({
				venueReadableId: this.venueReadableId,
				tableNumber: this.tableNumber,
			});
			await this.loadVenueAndTable(this.venueReadableId ?? '', this.tableNumber ?? '');

			interval(20_000)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(async () => {
					await this.loadChecks(this.venueReadableId ?? '', this.tableNumber ?? '');
				});
			await this.loadChecks(this.venueReadableId ?? '', this.tableNumber ?? '');

			this.loading = false;

			// Auto-start ordering if the pay app requested it and the table loaded successfully.
			if (shouldAutoOrder && this.venue && this.table) {
				console.log('Auto-order enabled, starting ordering...');
				setTimeout(() => {
					this.goToMenu();
				}, 500);
			}
		} catch (e) {
			this.loading = false;
		}
		this.repositoryService.onOrderChange(null);
		this.repositoryService.verifiedOrder.emit(null);
		this.cncDiscounts.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(discounts => {
			if (!discounts.length) {
				return;
			}
			this.handleDiscounts(discounts);
		});
		this.showAutomaticPromos();
	}

	private async showAutomaticPromos() {
		console.log('HomePage.handleShowAutomaticPromos()');
		if (!this.venue?._id) {
			return;
		}
		try {
			const promos = (await Api.getPromoCodesOfVenue(this.venue._id, true)).filter(it => it.isAutomatic);
			console.log('AutomaticPromos');
			console.log({
				automaticPromos: promos,
			});
			for (const promo of promos) {
				await AutomaticPromoModal.show(this.modalCtrl, promo);
			}
		} catch (e) {
			console.error('Failed to load automatic promos', e);
		}
	}

	async goToMenu() {
		if (!this.venue || !this.table) {
			this.snackBar.open(this.translate.instant('home_page.wait_loading'));
			return;
		}
		// Guard against re-entrancy: a second tap while ordering is in progress would start a
		// competing navigation and could create a second order.
		if (this.loading) {
			return;
		}
		this.loading = true;
		try {
			await this.repositoryService.createOrder(this.venue, null, OrderType.STANDARD, null, this.table);
			await MenuPage.navigate(this.router);
		} catch (e) {
			console.error('goToMenu failed', e);
			this.snackBar.open(this.translate.instant('home_page.scan_table_error'), null, {
				duration: 2000,
			});
		} finally {
			// Always clear loading so the button never gets stuck on its spinner, even if the
			// order creation or navigation fails or is redirected by a guard.
			this.loading = false;
		}
	}

	async loadVenueAndTable(venueReadableId: string, tableNumber: string) {
		console.log('loadVenueAndTable()', venueReadableId, tableNumber);
		this.loading = true;
		try {
			const venueData = await this.repositoryService.getVenue(venueReadableId);
			const table = (await Api.getTable(venueData._id, tableNumber)).data;
			if (venueData && table) {
				if (!table?.active) {
					this.loading = false;
					this.snackBar.open(this.translate.instant('home_page.scan_table_error'), null, {
						duration: 2000,
					});
					return;
				}
				this.repositoryService.venue.emit(venueData);
				this.repositoryService.table.next(table._id);
				this.venue = venueData;
				this.table = table;
			}
			this.loading = false;
		} catch (e) {
			console.error(e);
			this.snackBar.open(this.translate.instant('home_page.scan_table_error'), null, {
				duration: 2000,
			});
			this.loading = false;
		}
	}

	async loadChecks(venueReadableId: string, tableNumber: string) {
		console.log(`HomePage.loadChecks(venueReadableId: ${venueReadableId}, tableNumber: ${tableNumber})`);
		if (!venueReadableId || !tableNumber) {
			this.loading = false;
			return;
		}
		try {
			if (this.table.active !== true) {
				return;
			}
			this.checksResponse = await this.repositoryService.getChecksOfTable(venueReadableId, tableNumber);
		} catch (e) {
			this.checksResponse = {
				checks: [],
				status: CheckStatus.FINISHED,
			};
			console.log('No checks.');
		}
		this.loading = false;
	}

	/**
	 * Gets the order URL based on table number and environment
	 * For 200 series tables: redirects to internal TopGolf order app
	 * For other tables: uses the table's QR code URL
	 */
	getPaymentUrl(): string | undefined {
		if (!this.table?.qrCode) {
			return undefined;
		}

		// Use internal TopGolf order app for 200 series tables
		return `${environment.topgolfPaymentUrl}/${this.venueReadableId}/${this.tableNumber}?participate=true`;
	}
}
